import * as React from "react";
import { cn } from "@/lib/utils";

/** What a Field/FormRow hands down to the control it wraps.
 *  Controls read this so the label association, the `aria-describedby` link to
 *  the helper/error line, and the error styling happen once here instead of
 *  being re-wired (and half-forgotten) at every call site.
 *
 *  `id` is undefined in `group` mode — see the Field docblock. */
type FieldWiring = {
  id?: string;
  describedBy?: string;
  invalid: boolean;
};

const FieldContext = React.createContext<FieldWiring | null>(null);

/** Used by every control that can sit in a Field.
 *
 *  ID PRECEDENCE — inside a Field, the FIELD wins; outside one, the control's
 *  own `id` is used. That looks backwards until you notice context only flows
 *  downward: a Field renders `<label for={its own id}>` and cannot see that a
 *  child overrode it, so letting the child win would silently orphan the label
 *  and lose the association altogether. To pin a specific id, put it on the
 *  Field (`<Field id="x">`) and it reaches the control from here.
 *
 *  `describedBy` and `invalid` do go the other way — a control may add to or
 *  override what the Field says, because neither can orphan anything. */
export function useFieldControl(own: {
  id?: string;
  describedBy?: string;
  invalid?: boolean;
}): { id?: string; describedBy?: string; invalid: boolean } {
  const ctx = React.useContext(FieldContext);
  return {
    // In group mode ctx.id is undefined, so nothing claims it and the group's
    // label is a <span> referenced by aria-labelledby instead of a <label for>.
    id: ctx ? ctx.id : own.id,
    describedBy: own.describedBy ?? ctx?.describedBy,
    invalid: own.invalid ?? ctx?.invalid ?? false,
  };
}

/** Builds the ids + helper line shared by Field and FormRow. */
export function useFieldWiring(args: {
  id?: string;
  help?: React.ReactNode;
  error?: React.ReactNode;
  group?: boolean;
}) {
  const auto = React.useId();
  const id = args.id ?? auto;
  const messageId = `${id}-message`;
  const labelId = `${id}-label`;
  // An error replaces the helper text rather than stacking under it.
  const message = args.error ?? args.help;
  // `""` counts as absent — otherwise `error={validationMessage}` with an empty
  // string renders a blank helper line (and reserves its space) on a valid field.
  const hasMessage =
    message !== undefined && message !== null && message !== false && message !== "";
  return {
    id,
    labelId,
    message,
    hasMessage,
    messageId,
    isError: Boolean(args.error),
    wiring: {
      // Group mode hands out NO id: several controls share this Field, and they
      // cannot all carry one id.
      id: args.group ? undefined : id,
      describedBy: hasMessage ? messageId : undefined,
      invalid: Boolean(args.error),
    } satisfies FieldWiring,
  };
}

export const FieldProvider = FieldContext.Provider;

export type FieldProps = {
  label: React.ReactNode;
  /** Secondary line under the label, inside the label itself. */
  hint?: React.ReactNode;
  /** Helper text under the control. Replaced by `error` when that is set. */
  help?: React.ReactNode;
  /** Truthy switches the control to its error look and colours the message. */
  error?: React.ReactNode;
  /** Override the generated id (it is applied to the wrapped control). */
  id?: string;
  /**
   * Set when this labels SEVERAL controls, or one that carries its own visible
   * label (a radio group, a list of checkboxes, a Checkbox with children).
   *
   * Default (false) is the single-control case: a real `<label for>` bound to
   * exactly one control.
   *
   * Group mode instead renders `role="group"` + `aria-labelledby` pointing at a
   * `<span>`, because a `<label for>` can only ever address one element — with
   * several children it would either orphan (nothing claims the id) or collide
   * (every child claims the same id). It also avoids double-labelling a
   * Checkbox/Radio that already wraps its own text in a `<label>`.
   */
  group?: boolean;
  children?: React.ReactNode;
  className?: string;
};

/** Labelled form field: label (+ hint) above the control, helper/error below. */
export function Field({
  label,
  hint,
  help,
  error,
  id,
  group,
  children,
  className,
}: FieldProps) {
  const f = useFieldWiring({ id, help, error, group });

  const labelContent = (
    <>
      {label}
      {hint && <span className="vb-field__hint">{hint}</span>}
    </>
  );

  return (
    <div
      className={cn("vb-field", className)}
      role={group ? "group" : undefined}
      aria-labelledby={group ? f.labelId : undefined}
    >
      {group ? (
        <span id={f.labelId} className="vb-field__label">
          {labelContent}
        </span>
      ) : (
        <label className="vb-field__label" htmlFor={f.id}>
          {labelContent}
        </label>
      )}

      <FieldProvider value={f.wiring}>{children}</FieldProvider>

      {f.hasMessage && (
        <span
          id={f.messageId}
          className={cn("vb-field__help", f.isError && "vb-field__help--error")}
        >
          {f.message}
        </span>
      )}
    </div>
  );
}

export default Field;
